import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowUpRight, Search } from "lucide-react";
import { Header } from "../components/Header";
import { Ticker } from "../components/Ticker";
import { Footer } from "../components/Footer";
import SiteActionBar from "../components/SiteActionBar";
import HomeRotatingTiles from "../components/HomeRotatingTiles";
import HomeStrip from "../components/HomeStrip";
import HomepageBanner from "../components/HomepageBanner";
import { usePostTypes } from "../lib/postTypes";
import { getNews, getPosts, resolveAsset } from "../lib/api";
import { hasPostBody } from "../lib/posts";
import { usePageContent, kickerStyle, titleStyle } from "../context/PageContentContext";
import { useTheme } from "../context/ThemeContext";
import CardIntro from "../components/CardIntro";
import RightColumnWidgets from "../components/RightColumnWidgets";
import { api } from "../lib/api";

const typeKicker = {
  long: "Long read",
  short: "Short note",
  note: "Note",
};

// Mirrors Tailwind's `sm:` breakpoint (640px) so the homepage post count
// switches desktop/mobile at the same width the layout itself does.
function useIsMobileViewport() {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 639px)").matches,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = () => setIsMobile(mq.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return isMobile;
}

// Strip HTML tags + whitespace and check whether the post has any
// substantive body content (or a non-empty custom intro). Used to hide
// the "Read the post" CTA on headline-only posts (e.g. breaking news
// that the admin will fill in later). See /lib/posts.js for the shared
// implementation used across Home + Blast views.

const labelFor = (id, extraTypes) => {
  const match = (extraTypes || []).find((t) => t.id === id);
  if (match && match.label) return match.label;
  if (typeKicker[id]) return typeKicker[id];
  if (!id) return "Post";
  return String(id)
    .split(/[-_]/)
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
};

export default function Home() {
  const featuredCopy = usePageContent("home_featured");
  const explore = usePageContent("home_explore");
  const { theme } = useTheme();
  const { types: postTypes } = usePostTypes();
  const [tickerItems, setTickerItems] = useState([]);
  const [newsHeadlines, setNewsHeadlines] = useState(null);
  const [posts, setPosts] = useState(null);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [rightWidgets, setRightWidgets] = useState([]);
  const [heroBg, setHeroBg] = useState(null);
  // "Previous News" search input on the homepage. Submitting hops to
  // /archive?q=... which the ArchivePage already handles (prefills the
  // input + kicks off the site-wide search effect).
  const navigate = useNavigate();
  const [archiveQuery, setArchiveQuery] = useState("");
  const submitArchiveSearch = (e) => {
    e.preventDefault();
    const q = archiveQuery.trim();
    if (q) {
      navigate(`/archive?q=${encodeURIComponent(q)}#search`);
    } else {
      navigate("/archive#search");
    }
  };

  useEffect(() => {
    // Ordered list of right-column widgets (3 images + a countdown,
    // arbitrary order). Backend migrates legacy single-image/countdown
    // settings into the list on first read.
    api.get("/settings/homepage-right-column")
      .then((r) => {
        const list = Array.isArray(r.data?.widgets) ? r.data.widgets : [];
        // Per-widget visibility — admins can hide a widget without
        // deleting it. Treat missing/undefined as visible.
        setRightWidgets(list.filter((w) => w && w.enabled !== false));
      })
      .catch(() => setRightWidgets([]));
    api.get("/settings/homepage-hero-bg").then((r) => setHeroBg(r.data)).catch(() => setHeroBg({}));
  }, []);

  // Posts fetched on their OWN effect (not bundled behind Promise.all with
  // the news ticker) — the hero (LCP element) depends only on this, so it
  // must resolve and paint the moment /posts responds, never waiting on
  // the slower/optional news-ticker fetch below.
  useEffect(() => {
    getPosts()
      .then((p) => p || [])
      .catch(() => [])
      .then((allPosts) => setPosts(allPosts))
      .finally(() => setLoadingPosts(false));
  }, []);

  // News headlines are fetched independently, purely for the ticker —
  // cosmetic, below the fold on first paint — so it never gates the hero.
  useEffect(() => {
    getNews("all")
      .then((d) => d || [])
      .catch(() => [])
      .then((d) => setNewsHeadlines(d));
  }, []);

  // Merge posts + news into the ticker once BOTH are available — reuses
  // the `posts` state set above instead of re-fetching.
  useEffect(() => {
    if (posts === null || newsHeadlines === null) return;
    const postItems = posts.slice(0, 6).map((p) => ({
      id: `post-${p.id}`,
      title: p.title,
      source_name: "LAST",
      source_color: "#6CABDD",
      link: `/post/${p.slug || p.id}`,
      internal: true,
    }));
    const newsItems = newsHeadlines.slice(0, 10).map((n) => ({
      id: `news-${n.id}`,
      title: n.title,
      source_name: n.source_name,
      source_color: n.source_color,
      link: n.link,
      internal: false,
    }));
    // Round-robin merge so posts appear early and frequently
    const merged = [];
    const max = Math.max(postItems.length, newsItems.length);
    for (let i = 0; i < max; i += 1) {
      if (postItems[i]) merged.push(postItems[i]);
      if (newsItems[i]) merged.push(newsItems[i]);
    }
    setTickerItems(merged);
  }, [posts, newsHeadlines]);

  // Total post count is admin-configurable via Admin dashboard →
  // "Homepage post count" panel, with separate desktop/mobile values
  // (1–15). `isMobile` mirrors Tailwind's `sm:` breakpoint (640px) so it
  // lines up with the rest of the responsive layout below.
  const isMobile = useIsMobileViewport();
  const totalPosts = Math.max(
    1,
    Math.min(
      15,
      Number(isMobile ? theme.home_posts_count_mobile : theme.home_posts_count_desktop) || 7,
    ),
  );
  const featured = posts && posts.length > 0 ? posts[0] : null;
  const rest = posts && posts.length > 1 ? posts.slice(1, totalPosts) : [];

  return (
    <div className="min-h-screen bg-[var(--page-bg)] text-[var(--brand-ink)]">
      <Header mobileActionBarOnly />
      <Ticker items={tickerItems} />

      <HomeStrip />
      <HomepageBanner />

      {/* Featured editorial section */}
      <section
        className="mx-auto max-w-7xl px-5 pt-4 sm:px-8 sm:pt-10"
        data-testid="home-featured"
      >
        <div className="mb-4 flex items-end justify-between gap-4 sm:mb-8">
          <div className="min-w-0 flex-1">
            {loadingPosts ? (
              <div data-testid="home-header-skeleton" aria-hidden="true">
                <div className="h-3 w-24 animate-pulse rounded bg-black/10" />
                <div className="mt-3 h-9 w-3/4 animate-pulse rounded bg-black/10 sm:h-12" />
                <div className="mt-3 h-3 w-1/2 animate-pulse rounded bg-black/10" />
              </div>
            ) : (
              <>
                {featuredCopy.kicker ? (
                  <p className="label-kicker text-brand-accent" style={kickerStyle(featuredCopy)}>
                    {featuredCopy.kicker}
                  </p>
                ) : null}
                {/* SEO H1 — visually hidden but read by search engines.
                    Ensures the homepage's primary heading targets the
                    queries we want to rank for ("Coventry City fans",
                    "Coventry City fan hub") without changing the visible
                    LAteST-from-LAST design. */}
                <h1 className="sr-only">Coventry City Fan Hub — LAST · for Coventry City fans</h1>
                <h2
                  className="brand-headline whitespace-pre-line font-display text-3xl font-black tracking-tight sm:text-4xl lg:text-5xl"
                  style={titleStyle(featuredCopy)}
                >
                  {featured ? featuredCopy.title : "Welcome to LAST."}
                </h2>
                {featured ? (
                  featuredCopy.intro ? (
                    <p className="mt-2 max-w-xl text-sm text-[var(--brand-body)]">
                      {featuredCopy.intro}
                    </p>
                  ) : null
                ) : (
                  <p className="mt-2 max-w-xl text-sm text-[var(--brand-body)]">
                    The fan-run Coventry City hub. First post is on its way.
                  </p>
                )}
              </>
            )}
          </div>
          {/* Inline action bar — only on desktop. On mobile the standalone
              bar appears under the header instead (see above), so the
              title gets the full width on phones. */}
          <div className="hidden sm:flex">
            <SiteActionBar inline />
          </div>
        </div>

        {loadingPosts ? (
          <div
            className="grid grid-cols-12 gap-6"
            data-testid="home-posts-loading"
            aria-busy="true"
            aria-label="Loading posts"
          >
            <div className="col-span-12 overflow-hidden rounded-md border border-black/10 bg-white lg:col-span-8">
              <div className="aspect-[16/8] w-full animate-pulse bg-black/5" />
              <div className="flex flex-col gap-4 p-8">
                <div className="flex items-center gap-3">
                  <div className="h-5 w-20 animate-pulse rounded-sm bg-black/10" />
                  <div className="h-3 w-24 animate-pulse rounded bg-black/10" />
                </div>
                <div className="space-y-2">
                  <div className="h-8 w-5/6 animate-pulse rounded bg-black/10" />
                  <div className="h-8 w-3/4 animate-pulse rounded bg-black/10" />
                </div>
                <div className="space-y-2">
                  <div className="h-3 w-full animate-pulse rounded bg-black/10" />
                  <div className="h-3 w-11/12 animate-pulse rounded bg-black/10" />
                  <div className="h-3 w-2/3 animate-pulse rounded bg-black/10" />
                </div>
              </div>
            </div>
            <aside className="col-span-12 flex flex-col gap-4 lg:col-span-4">
              {[0, 1, 2].map((i) => (
                <div key={i} className="rounded-md border border-black/10 bg-white p-5">
                  <div className="flex items-center justify-between">
                    <div className="h-3 w-16 animate-pulse rounded bg-black/10" />
                    <div className="h-3 w-14 animate-pulse rounded bg-black/10" />
                  </div>
                  <div className="mt-3 space-y-2">
                    <div className="h-4 w-5/6 animate-pulse rounded bg-black/10" />
                    <div className="h-4 w-2/3 animate-pulse rounded bg-black/10" />
                  </div>
                </div>
              ))}
            </aside>
          </div>
        ) : !featured ? (
          <div
            className="rounded-md border border-dashed border-black/15 p-10 text-center"
            data-testid="home-posts-empty"
          >
            <p className="font-display text-lg font-bold">No posts yet.</p>
            <p className="mt-1 text-sm text-[var(--brand-body)]">
              Sign in at{" "}
              <Link to="/admin/login" className="underline decoration-[#6CABDD] underline-offset-4">
                /admin
              </Link>{" "}
              to publish the first one.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-12 gap-6 items-start">
            {/* Left Content Rail (Featured Hero + Recent previews grid + Search/Previous news) */}
            <div className="col-span-12 lg:col-span-8 flex flex-col gap-6">
              <div
                className={`card-hover group relative flex flex-col overflow-hidden rounded-md border border-white bg-white ${
                  hasPostBody(featured) ? "cursor-pointer" : "cursor-default select-text"
                }`}
                data-testid="home-featured-card"
              >
                {/* Stretched link — sits absolute over the whole card so
                    clicking anywhere navigates to the post. Inline links
                    inside the card-intro override this by sitting above
                    the link via `.card-intro a` (z-index in index.css).
                    We render the link FIRST so anchors elsewhere in the
                    tree paint on top of it without extra z-index work. */}
                {hasPostBody(featured) ? (
                  <Link
                    to={`/post/${featured.slug || featured.id}`}
                    aria-label={featured.title}
                    className="absolute inset-0 z-[1]"
                    data-testid="home-featured-stretched-link"
                  />
                ) : null}
                {/* Admin-configurable background painted INSIDE the hero
                    card, behind the cover image + text. Fully contained by
                    the card's rounded-md border so it doesn't bleed onto
                    the page outside. */}
                {heroBg && heroBg.image_url ? (
                  <img
                    src={heroBg.image_url}
                    alt=""
                    aria-hidden="true"
                    data-testid="home-hero-bg"
                    fetchPriority="high"
                    decoding="async"
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
                    style={{
                      opacity: Math.max(0, Math.min(1, (Number(heroBg.opacity) || 0) / 100)),
                    }}
                  />
                ) : null}
                {featured.cover_image ? (
                  <div
                    className="pointer-events-none relative z-[2] w-full overflow-hidden bg-[#F8FAFC]"
                    style={{ aspectRatio: featured.cover_aspect ?? 16 / 8 }}
                  >
                    <img
                      src={resolveAsset(featured.cover_image)}
                      alt=""
                      // This is the page's Largest Contentful Paint
                      // element — never lazy-load it, and tell the
                      // browser to fetch it before other same-priority
                      // resources.
                      loading="eager"
                      decoding="async"
                      fetchPriority="high"
                      className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                      style={{
                        objectPosition: `${featured.cover_focal_x ?? 50}% ${featured.cover_focal_y ?? 50}%`,
                      }}
                    />
                  </div>
                ) : null}
                <div className="pointer-events-none relative z-[2] flex flex-1 flex-col gap-4 p-8">
                  <div className="flex items-center gap-3">
                    <span className="rounded-sm bg-[#6CABDD] px-2 py-1 label-kicker text-white">
                      {labelFor(featured.post_type, postTypes)}
                    </span>
                    <span className="label-kicker text-[var(--brand-body)]">
                      {new Date(featured.display_at || featured.created_at).toLocaleDateString("en-GB", {
                        day: "numeric", month: "short", year: "numeric",
                      })}
                    </span>
                  </div>
                  <h2 className="font-display text-3xl font-black leading-[1.1] tracking-tight sm:text-4xl">
                    {featured.title}
                  </h2>
                  <CardIntro
                    post={featured}
                    maxLen={320}
                    className="line-clamp-3 text-base leading-relaxed text-[var(--brand-body)]"
                    data-testid="home-featured-intro"
                  />
                  {hasPostBody(featured) ? (
                    <span
                      data-testid="home-featured-read-cta"
                      className="mt-auto inline-flex items-center gap-1.5 font-display text-sm font-bold text-[var(--brand-ink)]"
                    >
                      Read the post
                      <ArrowUpRight
                        size={14}
                        className="transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                      />
                    </span>
                  ) : null}
                </div>
              </div>

              {/* Recent posts grid — sits directly below the hero card in the left rail */}
              {rest.length > 0 ? (
                <div
                  className="grid gap-4 sm:grid-cols-2"
                  data-testid="home-recent-posts"
                >
                  {rest.map((p) => {
                    const isClickable = hasPostBody(p);
                    const CardWrapper = isClickable ? Link : "div";
                    const extraProps = isClickable ? { to: `/post/${p.slug || p.id}` } : {};

                    return (
                      <CardWrapper
                        key={p.id}
                        {...extraProps}
                        className={`card-hover group block rounded-md border border-black/10 bg-white p-5 ${
                          isClickable ? "cursor-pointer" : "cursor-default select-text"
                        }`}
                        data-testid="home-recent-post"
                      >
                        <div className="flex items-center justify-between">
                          <span className="label-kicker text-[#6CABDD]">
                            {labelFor(p.post_type, postTypes)}
                          </span>
                          <span className="label-kicker text-[var(--brand-body)]">
                            {new Date(p.display_at || p.created_at).toLocaleDateString("en-GB")}
                          </span>
                        </div>
                        <div className="mt-2 flex gap-3">
                          {p.cover_image ? (
                            <div
                              className="h-16 w-16 flex-shrink-0 overflow-hidden rounded-sm bg-[#F8FAFC]"
                              aria-hidden="true"
                            >
                              <img
                                src={resolveAsset(p.cover_image)}
                                alt=""
                                loading="lazy"
                                className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
                                style={{
                                  objectPosition: `${p.cover_focal_x ?? 50}% ${p.cover_focal_y ?? 50}%`,
                                }}
                              />
                            </div>
                          ) : null}
                          <h3 className="line-clamp-3 flex-1 font-display text-lg font-bold leading-tight">
                            {p.title}
                          </h3>
                        </div>
                      </CardWrapper>
                    );
                  })}
                </div>
              ) : null}

              {/* "Previous News" search/archivelink block below the grid */}
              {featured ? (
                <div
                  className="flex flex-col-reverse items-stretch gap-4 lg:flex-row lg:items-end lg:justify-between"
                >
                  {/* Search block — mirror of the button, sits on the LEFT
                      on desktop and above the button on mobile. */}
                  <form
                    onSubmit={submitArchiveSearch}
                    className="w-full max-w-md"
                    data-testid="home-archive-search-form"
                  >
                    <label className="block">
                      <span className="label-kicker text-[var(--brand-body)]">
                        Search the whole site
                      </span>
                      <div className="mt-2 flex items-center gap-2 rounded-md border border-black/15 bg-white px-3 py-2 focus-within:border-[#0A0F17]">
                        <Search size={16} className="text-[var(--brand-body)]" />
                        <input
                          type="search"
                          value={archiveQuery}
                          onChange={(e) => setArchiveQuery(e.target.value)}
                          placeholder="Search posts, link directory, opponent, season…"
                          aria-label="Search the whole site"
                          data-testid="home-archive-search-input"
                          className="block w-full bg-transparent font-display text-sm outline-none placeholder:text-[#94A3B8]"
                        />
                        <button
                          type="submit"
                          data-testid="home-archive-search-submit"
                          className="rounded-sm bg-[#0A0F17] px-3 py-1.5 font-display text-xs font-bold uppercase tracking-wider text-white transition hover:opacity-90"
                        >
                          Search
                        </button>
                      </div>
                    </label>
                  </form>
                  <Link
                    to="/archive#search"
                    data-testid="home-archive-link"
                    className="inline-flex items-center justify-center gap-2 self-center rounded-md bg-[#0A0F17]/85 px-5 py-3 font-display text-base font-bold uppercase tracking-wider text-white shadow-md transition hover:bg-[#0A0F17] sm:text-lg lg:self-end"
                  >
                    Previous News
                    <ArrowUpRight size={18} strokeWidth={2.5} />
                  </Link>
                </div>
              ) : null}
            </div>

            {/* Right Content Sidebar Rail (Homepage Widgets) */}
            <RightColumnWidgets
              className="col-span-12 flex flex-col gap-4 lg:col-span-4"
              testId="home-right-widgets"
              widgets={rightWidgets}
            />
          </div>
        )}
      </section>

      {/* Section links */}
      <section
        className="mx-auto max-w-7xl px-5 py-6 sm:px-8 sm:py-8"
        data-testid="home-hub"
      >
        <div className="mb-6 border-t border-black/10 pt-6">
          <p className="label-kicker text-brand-accent" style={kickerStyle(explore)}>
            {explore.kicker}
          </p>
          <h2
            className="brand-headline mt-2 whitespace-pre-line font-display text-2xl font-black tracking-tight sm:text-3xl"
            style={titleStyle(explore)}
          >
            {explore.title}
          </h2>
          {explore.intro ? (
            <p className="mt-2 max-w-xl text-sm text-[var(--brand-body)]">
              {explore.intro}
            </p>
          ) : null}
        </div>

        <HomeRotatingTiles />
      </section>

      <Footer />
    </div>
  );
}
